
import React, { useMemo, useState } from 'react';
import { Student, AgendaEntry, AttendanceRecord, AssessmentGrade, SasGrade, FormativeGradeTP, FormativeLevel, LearningObjective, AttendanceStatus, ScheduleEntry, Teacher, Tugas, TugasSubmission, UserRole, LearningContent, ActivityType } from '../types';
import { 
  ArrowLeft, 
  User, 
  Calendar, 
  ClipboardList, 
  TrendingUp, 
  CheckCircle, 
  XCircle, 
  Clock,
  Award,
  BookOpen,
  ExternalLink,
  Eye,
  EyeOff,
  Target,
  Sparkles,
  GraduationCap,
  Loader2,
  Lock,
  ArrowRight,
  BarChart3,
  CheckCircle2,
  FileText
} from 'lucide-react';
import TugasView from './TugasView';

interface Props {
  student: Student | null;
  students?: Student[];
  agendas: AgendaEntry[];
  attendanceRecords: AttendanceRecord[];
  assessmentGrades: AssessmentGrade[];
  sasGrades: SasGrade[];
  formativeGradesTP: FormativeGradeTP[];
  objectives: LearningObjective[];
  schedules: ScheduleEntry[];
  teachers: Teacher[];
  teacherSubject?: string;
  onBack: () => void;
  // Tugas props
  tugas?: Tugas[];
  tugasSubmissions?: TugasSubmission[];
  onSaveTugas?: (tugas: Tugas) => void;
  onDeleteTugas?: (id: string) => void;
  onSubmitTugas?: (submission: TugasSubmission) => void;
  role?: UserRole;
  schoolSettings?: any;
  learningContents?: LearningContent[];
  onFetchOnDemand?: (target: 'learningObjectives' | 'agendas' | 'learningContents' | 'attendanceRecords') => Promise<void>;
  initialTab?: 'profil' | 'nilai';
  onTabChange?: (tab: 'profil' | 'nilai') => void;
}

const StudentProfileView: React.FC<Props> = ({ 
  student, 
  students = [],
  agendas, 
  attendanceRecords, 
  assessmentGrades, 
  sasGrades,
  formativeGradesTP, 
  objectives,
  schedules,
  teachers,
  teacherSubject,
  onBack,
  tugas = [],
  tugasSubmissions = [],
  learningContents = [],
  onSaveTugas = () => {},
  onDeleteTugas = () => {},
  onSubmitTugas = () => {},
  role = UserRole.Guru,
  schoolSettings,
  onFetchOnDemand,
  initialTab = 'profil',
  onTabChange
}) => {
  if (!student) return null;

  const [activeTab, setActiveTab] = useState<'profil' | 'nilai'>(initialTab);

  React.useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  const handleTabChange = (tab: 'profil' | 'nilai') => {
    setActiveTab(tab);
    if (onTabChange) {
      onTabChange(tab);
    }
  };
  const [selectedSubject, setSelectedSubject] = useState<string>(teacherSubject || '');
  const [isLoadingMaterials, setIsLoadingMaterials] = useState(false);
  const [isLoadingAttendance, setIsLoadingAttendance] = useState(false);
  const hasFetchedRef = React.useRef<string>('');

  React.useEffect(() => {
    if (role === UserRole.Siswa && onFetchOnDemand && student) {
      if (hasFetchedRef.current === student.id) return;
      hasFetchedRef.current = student.id;

      setIsLoadingAttendance(true);
      setIsLoadingMaterials(true);
      Promise.all([
        onFetchOnDemand('attendanceRecords'),
        onFetchOnDemand('agendas'),
        onFetchOnDemand('learningObjectives'),
        onFetchOnDemand('learningContents')
      ]).finally(() => {
        setIsLoadingAttendance(false);
        setIsLoadingMaterials(false);
      });
    }
  }, [role, student?.id, onFetchOnDemand]);

  const isGradeVisible = useMemo(() => {
    if (role !== UserRole.Siswa) return true;
    if (schoolSettings?.showGradesToStudents === false) return false;
    
    if (selectedSubject) {
      const teacher = teachers.find(t => 
        t.subject === selectedSubject && 
        (Array.isArray(t.assignedClasses) ? t.assignedClasses.includes(student.class) : false)
      );
      if (teacher && teacher.GradeVisibility === false) return false;
    }
    return true;
  }, [role, schoolSettings, selectedSubject, teachers, student.class]);

  const isRaporVisible = useMemo(() => {
    if (role !== UserRole.Siswa) return true;
    if (schoolSettings?.showGradesToStudents === false) return false;
    
    if (selectedSubject) {
      const teacher = teachers.find(t => 
        t.subject === selectedSubject && 
        (Array.isArray(t.assignedClasses) ? t.assignedClasses.includes(student.class) : false)
      );
      if (teacher && teacher.RaporVisibility === false) return false;
    }
    return true;
  }, [role, schoolSettings, selectedSubject, teachers, student.class]);

  const isSasVisible = useMemo(() => {
    if (role !== UserRole.Siswa) return true;
    if (schoolSettings?.showGradesToStudents === false) return false;
    
    if (selectedSubject) {
      const teacher = teachers.find(t => 
        t.subject === selectedSubject && 
        (Array.isArray(t.assignedClasses) ? t.assignedClasses.includes(student.class) : false)
      );
      if (teacher && teacher.SasVisibility === false) return false;
    }
    return true;
  }, [role, schoolSettings, selectedSubject, teachers, student.class]);

  const getEmbeddableLink = (url: string) => {
    if (!url) return null;
    try {
      const urlObj = new URL(url);
      if (urlObj.hostname.includes('youtube.com') || urlObj.hostname.includes('youtu.be')) {
        const videoId = urlObj.searchParams.get('v') || urlObj.pathname.split('/').pop();
        return `https://www.youtube.com/embed/${videoId}`;
      }
      if (urlObj.hostname.includes('docs.google.com') || urlObj.hostname.includes('drive.google.com')) {
        if (url.includes('/edit')) {
          return url.replace('/edit', '/preview');
        }
        if (url.includes('/view')) {
          return url.replace('/view', '/preview');
        }
        if (url.includes('drive.google.com/file/d/')) {
          const match = url.match(/\/d\/(.*?)\//);
          if (match && match[1]) {
            return `https://drive.google.com/file/d/${match[1]}/preview`;
          }
        }
        return url;
      }
      if (urlObj.hostname.includes('canva.com') && url.includes('/view')) {
        return url;
      }
      return null;
    } catch {
      return null;
    }
  };

  const isSubjectMatching = (s1?: string, s2?: string) => {
    if (!s1 || !s2) return false;
    return s1.trim().toLowerCase() === s2.trim().toLowerCase();
  };

  const isClassMatching = (targetClass?: string | null, studentClass?: string | null): boolean => {
    if (!studentClass || !targetClass) return false;
    const clean = (s: string) => s.trim().toLowerCase().replace(/^kelas\s*/i, '').replace(/[\s\-_]+/g, '');
    const normTarget = clean(targetClass);
    const normStudent = clean(studentClass);
    if (!normTarget || !normStudent) return false;
    if (normTarget === normStudent) return true;
    if (normTarget === 'semua' || normTarget === 'semuakelompok' || normTarget === 'semuakelas') return true;
    const targetList = targetClass.split(',').map(s => clean(s));
    if (targetList.includes(normStudent)) return true;
    return false;
  };

  const getStudentScore = (scores: Record<string, number> | undefined, std: Student): number | undefined => {
    if (!scores || typeof scores !== 'object') return undefined;
    if (scores[std.id] !== undefined && scores[std.id] !== null) return Number(scores[std.id]);
    if (std.nisn && scores[std.nisn] !== undefined && scores[std.nisn] !== null) return Number(scores[std.nisn]);
    if (std.nis && scores[std.nis] !== undefined && scores[std.nis] !== null) return Number(scores[std.nis]);
    if (scores[String(std.id)] !== undefined && scores[String(std.id)] !== null) return Number(scores[String(std.id)]);
    return undefined;
  };

  const isStudentMatch = (studentIdInRecord: string | undefined, std: Student): boolean => {
    if (!studentIdInRecord) return false;
    const sid = String(studentIdInRecord).trim();
    if (sid === String(std.id).trim()) return true;
    if (std.nisn && sid === String(std.nisn).trim()) return true;
    if (std.nis && sid === String(std.nis).trim()) return true;
    return false;
  };

  const availableSubjects = useMemo(() => {
    if (!student) return [];
    const subjectsFromSchedules = (schedules || []).filter(s => isClassMatching(s.class, student.class)).map(s => s.subject);
    const subjectsFromAgendas = (agendas || []).filter(a => isClassMatching(a.class, student.class)).map(a => a.subject);
    const subjectsFromTeachers = (teachers || []).filter(t => (t.assignedClasses || []).some(c => isClassMatching(c, student.class))).map(t => t.subject);
    const subjectsFromObjectives = (objectives || []).map(o => o.subject);
    const subjectsFromGrades = (assessmentGrades || []).map(g => {
      const tp = (objectives || []).find(o => o.id === g.tpId);
      return tp?.subject || g.subject;
    }).filter(Boolean) as string[];
    const subjectsFromSas = (sasGrades || [])
      .filter(s => isStudentMatch(s.studentId, student))
      .map(s => s.subject);
    
    // Extract subjects from student attendance records
    const studentRecords = (attendanceRecords || []).filter(r => isStudentMatch(r.studentId, student));
    const subjectsFromAttendance = studentRecords.map(r => {
      const agenda = (agendas || []).find(a => a.id === r.agendaId);
      if (agenda?.subject) return agenda.subject;
      if (r.teacherId) return (teachers || []).find(t => t.id === r.teacherId)?.subject;
      return null;
    }).filter(Boolean) as string[];

    const rawList = [
      ...subjectsFromSchedules, 
      ...subjectsFromAgendas, 
      ...subjectsFromTeachers,
      ...subjectsFromObjectives,
      ...subjectsFromGrades,
      ...subjectsFromSas,
      ...subjectsFromAttendance
    ].filter(Boolean);

    const seen = new Set<string>();
    const uniqList: string[] = [];
    rawList.forEach(s => {
      const cleaned = s.trim();
      const lower = cleaned.toLowerCase();
      if (!seen.has(lower)) {
        seen.add(lower);
        uniqList.push(cleaned);
      }
    });
    return uniqList;
  }, [schedules, agendas, teachers, student, attendanceRecords, objectives, assessmentGrades, sasGrades]);

  React.useEffect(() => {
    if (teacherSubject && teacherSubject !== selectedSubject) {
      setSelectedSubject(teacherSubject);
    }
  }, [teacherSubject]);

  React.useEffect(() => {
    if (!selectedSubject && availableSubjects.length > 0) {
      setSelectedSubject(availableSubjects[0]);
    }
  }, [availableSubjects, selectedSubject]);

  const studentAttendance = useMemo(() => {
    if (!student) return [];
    return (attendanceRecords || []).filter(r => {
      if (!isStudentMatch(r.studentId, student)) return false;
      if (r.class && !isClassMatching(r.class, student.class)) return false;
      return true;
    });
  }, [attendanceRecords, student]);

  const stats = useMemo(() => {
    const subjects = selectedSubject ? [selectedSubject] : availableSubjects;
    
    return subjects.map(subject => {
      // Find all agendas for this subject & student's class where attendance was actually conducted
      const subjectAgendas = (agendas || []).filter(a => {
        if (!student?.class) return false;
        if (!isClassMatching(a.class, student.class)) return false;
        if (!isSubjectMatching(a.subject, subject)) return false;

        const isJamKosong = a.status === 'Jam kosong';
        const isNoAbsenceActivity = [
          ActivityType.KEGIATAN_LOMBA, 
          ActivityType.KEGIATAN_KESISWAAN, 
          ActivityType.KEGIATAN_SEKOLAH, 
          ActivityType.SAS
        ].includes(a.activityType as any) || 
        (a.activityType as any) === 'SAS' || 
        (a.activityType as any) === 'Sumatif Akhir' ||
        a.isAttendanceOptional;

        const hasAttendanceRecord = (attendanceRecords || []).some(r => r.agendaId === a.id);

        return !isJamKosong && !isNoAbsenceActivity && hasAttendanceRecord;
      });

      // Find all attendance records for this student & subject
      const filteredAttendance = studentAttendance.filter(r => {
        if (r.class && !isClassMatching(r.class, student?.class)) {
          return false;
        }
        const agenda = (agendas || []).find(a => a.id === r.agendaId);
        if (agenda) {
          if (agenda.class && !isClassMatching(agenda.class, student?.class)) {
            return false;
          }
          if (agenda.subject) {
            return isSubjectMatching(agenda.subject, subject);
          }
        }
        if ((r as any).subject) {
          return isSubjectMatching((r as any).subject, subject);
        }
        if (r.teacherId) {
          const teacher = (teachers || []).find(t => t.id === r.teacherId);
          if (teacher && teacher.subject) {
            return isSubjectMatching(teacher.subject, subject);
          }
        }
        if (availableSubjects.length === 1 && availableSubjects[0] === subject) {
          return true;
        }
        return false;
      });

      const processedAgendaIds = new Set<string>();
      const processedDates = new Set<string>();

      // 1. Convert subject agendas into history items
      const historyListFromAgendas = subjectAgendas.map(agenda => {
        if (agenda.id) processedAgendaIds.add(agenda.id);
        if (agenda.date) processedDates.add(agenda.date);

        const rec = filteredAttendance.find(r => 
          (r.agendaId && r.agendaId === agenda.id) || 
          (r.date && r.date === agenda.date)
        );

        const tpObj = agenda.tpId ? (objectives || []).find(o => o.id === agenda.tpId) : null;
        const topicText = agenda.topic || agenda.tpDescription || (tpObj ? (tpObj.code ? `${tpObj.code}: ${tpObj.description}` : tpObj.description) : '') || agenda.notes || 'Pertemuan Pembelajaran';
        const material = agenda.materialLink || (agenda as any).linkMateri || (agenda as any).link || '';

        return {
          id: rec?.id || agenda.id,
          date: agenda.date || rec?.date || 'Tanpa Tanggal',
          agendaTitle: topicText,
          topic: topicText,
          materialLink: material,
          status: rec?.status || AttendanceStatus.Hadir,
          description: rec?.description || agenda.notes || '',
          subject: subject
        };
      });

      // 2. Add attendance records that were not covered by an agenda entry
      const unmatchedAttendance = filteredAttendance.filter(r => 
        (!r.agendaId || !processedAgendaIds.has(r.agendaId)) && 
        (!r.date || !processedDates.has(r.date))
      );

      const historyListFromRecords = unmatchedAttendance.map(r => {
        const agenda = (agendas || []).find(a => 
          a.id === r.agendaId || 
          (a.date === r.date && student?.class && isClassMatching(a.class, student.class) && isSubjectMatching(a.subject, subject))
        );
        const tpObj = agenda?.tpId ? (objectives || []).find(o => o.id === agenda.tpId) : null;
        const topicText = agenda?.topic || agenda?.tpDescription || (tpObj ? (tpObj.code ? `${tpObj.code}: ${tpObj.description}` : tpObj.description) : '') || r.description || 'Pertemuan Pembelajaran';
        const material = agenda?.materialLink || (agenda as any)?.linkMateri || (agenda as any)?.link || '';

        return {
          id: r.id,
          date: r.date || agenda?.date || 'Tanpa Tanggal',
          agendaTitle: topicText,
          topic: topicText,
          materialLink: material,
          status: r.status,
          description: r.description || agenda?.notes || '',
          subject: subject
        };
      });

      const fullHistoryList = [...historyListFromAgendas, ...historyListFromRecords]
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      const total = fullHistoryList.length;
      const hadir = fullHistoryList.filter(r => {
        const s = (r.status || '').toString().trim().toLowerCase();
        return s === AttendanceStatus.Hadir.toLowerCase() || 
               s === AttendanceStatus.Terlambat.toLowerCase() || 
               s === AttendanceStatus.Pulang.toLowerCase();
      }).length;
      const izin = fullHistoryList.filter(r => (r.status || '').toString().trim().toLowerCase() === AttendanceStatus.Izin.toLowerCase()).length;
      const sakit = fullHistoryList.filter(r => (r.status || '').toString().trim().toLowerCase() === AttendanceStatus.Sakit.toLowerCase()).length;
      const alpa = fullHistoryList.filter(r => (r.status || '').toString().trim().toLowerCase() === AttendanceStatus.Alpa.toLowerCase()).length;
      const dispen = fullHistoryList.filter(r => (r.status || '').toString().trim().toLowerCase() === AttendanceStatus.Dispen.toLowerCase()).length;

      const validPresence = hadir + dispen;
      const attendanceRate = total > 0 ? Math.round((validPresence / total) * 100) : 0;

      return { subject, total, hadir, izin, sakit, alpa, dispen, attendanceRate, historyList: fullHistoryList };
    });
  }, [studentAttendance, agendas, teachers, selectedSubject, availableSubjects, student, objectives, attendanceRecords]);

  const getSubjectRaporCalculation = (subj: string) => {
    const currentTeacher = teachers.find(t => 
      isSubjectMatching(t.subject, subj) && 
      (Array.isArray(t.assignedClasses) ? t.assignedClasses.some(c => isClassMatching(c, student.class)) : false)
    ) || teachers.find(t => isSubjectMatching(t.subject, subj));

    const weights = { 
      sumatif: currentTeacher?.raporSumatifWeight ?? 40, 
      praktikum: currentTeacher?.raporPraktikumWeight ?? 30,
      sas: currentTeacher?.raporSasWeight ?? 30 
    };

    const savedDaily = localStorage.getItem('grading_weight_daily');
    const savedSas = localStorage.getItem('grading_weight_sas');
    const weightDaily = currentTeacher?.weightDaily ?? (savedDaily ? Number(savedDaily) : 20);
    const weightSas = currentTeacher?.weightSas ?? (savedSas ? Number(savedSas) : 80);

    // 1. Notebook TTD data
    let sasNotebookEntries: Record<string, { jumlahTtd: number }> = {};
    let sasNotebookMaxTtd = 0;
    const teacherId = currentTeacher?.id || '';
    if (teacherId) {
      const keysToTry = [
        `smpn1_notebook_rekap_${teacherId}_${student.class}`,
        `smpn1_notebook_rekap_${teacherId}_${student.class.replace(/\s+/g, '')}`,
        `smpn1_notebook_rekap_${teacherId}_${student.class.replace(/^(\d+)([A-Za-z]+)$/, '$1 $2')}`
      ];
      for (const k of keysToTry) {
        try {
          const saved = localStorage.getItem(k);
          if (saved) {
            const parsed = JSON.parse(saved);
            if (parsed) {
              const entriesObj = parsed.entries || parsed;
              if (entriesObj && typeof entriesObj === 'object' && Object.keys(entriesObj).length > 0) {
                sasNotebookEntries = entriesObj;
                break;
              }
            }
          }
        } catch (e) {}
      }
    }
    const classStudents = (students || []).filter(s => isClassMatching(s.class, student.class) && s.status === 'Aktif');
    classStudents.forEach(s => {
      const entry = sasNotebookEntries[s.id] || sasNotebookEntries[s.nisn] || sasNotebookEntries[s.nis] || { jumlahTtd: 0 };
      const ttdVal = entry.jumlahTtd || 0;
      if (ttdVal > sasNotebookMaxTtd) sasNotebookMaxTtd = ttdVal;
    });
    const studentEntry = sasNotebookEntries[student.id] || sasNotebookEntries[student.nisn] || sasNotebookEntries[student.nis] || { jumlahTtd: 0 };
    const ttd = studentEntry.jumlahTtd || 0;
    const nilaiCatatan = sasNotebookMaxTtd > 0 ? Math.round((ttd / sasNotebookMaxTtd) * 100) : 0;

    // 2. Quiz / Tugas Harian
    const classQuizGrades = (assessmentGrades || []).filter(g => {
      const tp = (objectives || []).find(o => o.id === g.tpId);
      const gradeSubj = tp?.subject || g.subject;
      if (gradeSubj && !isSubjectMatching(gradeSubj, subj)) return false;
      if (!isClassMatching(g.class, student.class) && g.class !== 'Semua Kelas') return false;
      return ((g.activityType as any) === 'KBM dan Quiz' || 
              (g.activityType as any) === 'Pembelajaran dan Asesmen' || 
              (g.activityType as any) === 'KBM dan Asesmen Harian' || 
              g.activityType === ActivityType.KBM_DAN_QUIZ || 
              g.activityType === ActivityType.KBM);
    });
    const totalQuizAgendas = classQuizGrades.length;
    let sumQuizScores = 0;
    classQuizGrades.forEach(g => {
      const score = getStudentScore(g.scores, student);
      const num = Number(score);
      if (!isNaN(num) && score !== undefined && score !== null && String(score) !== '') {
        sumQuizScores += num;
      }
    });
    const nilaiTh = totalQuizAgendas > 0 ? Math.round(sumQuizScores / totalQuizAgendas) : 0;

    // 3. Raw SAS score
    const studentSas = (sasGrades || []).filter(g => isStudentMatch(g.studentId, student) && isSubjectMatching(g.subject, subj));
    const sasObj = studentSas.length > 0 ? studentSas[studentSas.length - 1] : null;
    let rawSasScore = sasObj?.score ?? 0;
    const sasGradesFromAssessment = (assessmentGrades || []).filter(g => {
      const tp = (objectives || []).find(o => o.id === g.tpId);
      const gradeSubj = tp?.subject || g.subject;
      if (gradeSubj && !isSubjectMatching(gradeSubj, subj)) return false;
      return (isClassMatching(g.class, student.class) || g.class === 'Semua Kelas') && g.activityType === ActivityType.SAS;
    });
    if (sasGradesFromAssessment.length > 0) {
      const lastAssessmentSas = sasGradesFromAssessment[sasGradesFromAssessment.length - 1];
      const score = getStudentScore(lastAssessmentSas.scores, student);
      if (score !== undefined && score !== null && !isNaN(score)) {
        rawSasScore = score;
      }
    }

    // 4. Nilai Evaluasi (Sumatif Akhir) & Nilai Rapor dari Firebase
    const nilaiEvaluasi = Math.round(sasObj?.nilaiEvaluasi ?? rawSasScore ?? 0);
    const rawRapor = sasObj?.nilaiRapor ?? sasObj?.nilaiEvaluasi ?? rawSasScore ?? 0;
    const roundedFinal = Math.round(rawRapor);

    // 5. TP Sumatif and Praktikum averages
    const tpGrades: Record<string, { sumatif: number[], praktikum: number[] }> = {};
    const tpBreakdownArr: Array<{ tpId: string, tpCode: string, type: 'Sumatif' | 'Praktik', score: number }> = [];

    (assessmentGrades || []).forEach(g => {
      const score = getStudentScore(g.scores, student);
      if (score === undefined || score === null || isNaN(score)) return;
      if (g.activityType === ActivityType.SAS) return;

      const isTugasHarian = 
        g.activityType === ActivityType.PEMBELAJARAN_DAN_ASESMEN ||
        g.activityType === ActivityType.KBM_DAN_QUIZ ||
        g.activityType === ActivityType.KBM ||
        g.activityType === ActivityType.TUGAS_MANDIRI ||
        (g.activityType as any) === 'KBM dan Asesmen Harian' ||
        (g.activityType as any) === 'KBM dan Quiz' ||
        (g.activityType as any) === 'Tugas Harian' ||
        (g.activityType as any) === 'Penilaian Tugas Harian';

      if (isTugasHarian) return;

      const tp = (objectives || []).find(o => o.id === g.tpId);
      const gradeSubj = tp?.subject || g.subject;
      if (gradeSubj && !isSubjectMatching(gradeSubj, subj)) return;
      if (!isClassMatching(g.class, student.class) && g.class !== 'Semua Kelas') return;

      if (!g.tpId) return;

      const code = tp?.code || 'TP';
      if (!tpGrades[g.tpId]) {
        tpGrades[g.tpId] = { sumatif: [], praktikum: [] };
      }

      if (g.activityType === ActivityType.PRAKTIKUM && (tp ? tp.isPracticum : true)) {
        tpGrades[g.tpId].praktikum.push(score);
        tpBreakdownArr.push({ tpId: g.tpId, tpCode: code, type: 'Praktik', score });
      } else if (g.activityType === ActivityType.SUMATIF && (tp ? tp.isSummative : true)) {
        tpGrades[g.tpId].sumatif.push(score);
        tpBreakdownArr.push({ tpId: g.tpId, tpCode: code, type: 'Sumatif', score });
      } else if (!tp) {
        tpGrades[g.tpId].sumatif.push(score);
        tpBreakdownArr.push({ tpId: g.tpId, tpCode: code, type: 'Sumatif', score });
      }
    });

    tpBreakdownArr.sort((a, b) => (a.tpCode || '').localeCompare(b.tpCode || '', undefined, { numeric: true, sensitivity: 'base' }));

    const sumatifAverages: number[] = [];
    const praktikumAverages: number[] = [];

    Object.entries(tpGrades).forEach(([_, data]) => {
      if (data.sumatif.length > 0) {
        sumatifAverages.push(data.sumatif.reduce((a, b) => a + b, 0) / data.sumatif.length);
      }
      if (data.praktikum.length > 0) {
        praktikumAverages.push(data.praktikum.reduce((a, b) => a + b, 0) / data.praktikum.length);
      }
    });

    const avgSumatif = sumatifAverages.length > 0 ? sumatifAverages.reduce((a, b) => a + b, 0) / sumatifAverages.length : 0;
    const avgPraktikum = praktikumAverages.length > 0 ? praktikumAverages.reduce((a, b) => a + b, 0) / praktikumAverages.length : 0;

    const final = roundedFinal;

    return {
      teacherName: currentTeacher?.name || 'Guru Pengampu',
      weights,
      tpBreakdown: tpBreakdownArr,
      sasScore: nilaiEvaluasi,
      final: roundedFinal,
      tpCount: tpBreakdownArr.length,
      currentTeacher,
      rawSasScore
    };
  };

  const studentGrades = useMemo(() => {
    const summative: Array<{ tpId: string; type: ActivityType; score: number; date: string }> = [];

    (assessmentGrades || []).forEach(g => {
      const score = getStudentScore(g.scores, student);
      if (score === undefined || score === null || isNaN(score)) return;

      const tp = (objectives || []).find(o => o.id === g.tpId);
      const subj = tp?.subject || g.subject;
      if (selectedSubject && subj && !isSubjectMatching(subj, selectedSubject)) return;

      const isTugasHarian = 
        g.activityType === ActivityType.PEMBELAJARAN_DAN_ASESMEN ||
        g.activityType === ActivityType.KBM_DAN_QUIZ ||
        g.activityType === ActivityType.KBM ||
        g.activityType === ActivityType.TUGAS_MANDIRI ||
        (g.activityType as any) === 'KBM dan Asesmen Harian' ||
        (g.activityType as any) === 'KBM dan Quiz' ||
        (g.activityType as any) === 'Tugas Harian' ||
        (g.activityType as any) === 'Penilaian Tugas Harian';

      if (g.activityType !== ActivityType.SAS && !isTugasHarian) {
        summative.push({
          tpId: g.tpId,
          type: g.activityType,
          score,
          date: g.date
        });
      }
    });

    const formative = (formativeGradesTP || [])
      .filter(g => isClassMatching(g.class, student.class))
      .map(g => {
        const tp = (objectives || []).find(o => o.id === g.tpId);
        if (selectedSubject && tp && !isSubjectMatching(tp.subject, selectedSubject)) return null;
        let level = undefined;
        if (g.levels) {
          level = g.levels[student.id] || (student.nisn ? g.levels[student.nisn] : undefined) || (student.nis ? g.levels[student.nis] : undefined);
        }
        if (!level) return null;
        return { tpId: g.tpId, level };
      }).filter(Boolean) as Array<{ tpId: string; level: FormativeLevel }>;

    const subjectToUse = selectedSubject || availableSubjects[0] || '';
    const { sasScore, final } = getSubjectRaporCalculation(subjectToUse);

    return { summative, formative, sas: { score: sasScore }, final };
  }, [assessmentGrades, formativeGradesTP, sasGrades, student, selectedSubject, objectives, teachers, availableSubjects, students]);

  const allSubjectsGrades = useMemo(() => {
    return availableSubjects.map(subj => {
      const { teacherName, weights, tpBreakdown, sasScore, final, currentTeacher } = getSubjectRaporCalculation(subj);

      let gradeVisible = true;
      let raporVisible = true;
      let sasVisible = true;

      if (role === UserRole.Siswa) {
        if (schoolSettings?.showGradesToStudents === false) {
          gradeVisible = false;
          raporVisible = false;
          sasVisible = false;
        } else if (currentTeacher) {
          if (currentTeacher.GradeVisibility === false) gradeVisible = false;
          if (currentTeacher.RaporVisibility === false) raporVisible = false;
          if (currentTeacher.SasVisibility === false) sasVisible = false;
        }
      }

      return {
        subject: subj,
        teacherName,
        weights,
        tpBreakdown,
        sasScore,
        final,
        gradeVisible,
        raporVisible,
        sasVisible,
        tpCount: tpBreakdown.length
      };
    });
  }, [availableSubjects, teachers, student, assessmentGrades, sasGrades, objectives, role, schoolSettings, students]);

  return (
    <div className="animate-fade-in space-y-6 pb-24 sm:pb-12 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header & Navigation Tabs */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mt-2">
        <div className="flex items-center gap-4">
          {role === UserRole.Guru && (
            <button 
              onClick={onBack}
              className="p-2 sm:p-2.5 bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-all hover:scale-105 active:scale-95"
            >
              <ArrowLeft size={18} />
            </button>
          )}
          <div>
            <span className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest block mb-0.5">Portal Informasi Siswa</span>
            <h2 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight leading-none">
              {role === UserRole.Siswa ? 'Profil & Progress Belajar' : `Detail Siswa: ${student.name}`}
            </h2>
          </div>
        </div>

        {/* Tab Switcher Menu Buttons */}
        <div className="flex items-center gap-1.5 p-1.5 bg-slate-100 dark:bg-slate-900/90 rounded-2xl border border-slate-200/80 dark:border-slate-800/80 self-start md:self-auto shadow-inner">
          <button
            type="button"
            onClick={() => handleTabChange('profil')}
            className={`px-4 py-2.5 rounded-xl text-xs font-extrabold flex items-center gap-2 transition-all duration-200 cursor-pointer ${
              activeTab === 'profil'
                ? 'bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-md border border-slate-200/80 dark:border-slate-700/80'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
          >
            <User size={16} />
            <span>Profil & Presensi</span>
          </button>

          <button
            type="button"
            onClick={() => handleTabChange('nilai')}
            className={`px-4 py-2.5 rounded-xl text-xs font-extrabold flex items-center gap-2 transition-all duration-200 cursor-pointer ${
              activeTab === 'nilai'
                ? 'bg-gradient-to-r from-indigo-600 to-indigo-700 text-white shadow-md shadow-indigo-500/25 border border-indigo-500/30'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
          >
            <Award size={16} className={activeTab === 'nilai' ? 'text-amber-300' : ''} />
            <span>Nilai Saya</span>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-black ${
              activeTab === 'nilai' ? 'bg-amber-400 text-slate-950' : 'bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
            }`}>
              {allSubjectsGrades.length}
            </span>
          </button>
        </div>
      </div>

      {/* Hero Student Profile Banner Card */}
      <div className="relative overflow-hidden bg-white dark:bg-slate-900 rounded-3xl border border-slate-200/80 dark:border-slate-800 shadow-sm p-6 sm:p-7">
        <div className="absolute top-0 right-0 -mt-12 -mr-12 w-64 h-64 bg-indigo-500/5 dark:bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />
        
        <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            {/* Student Avatar Icon */}
            <div className="relative shrink-0">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-tr from-indigo-600 to-indigo-500 text-white rounded-2xl flex items-center justify-center font-black text-2xl shadow-lg shadow-indigo-500/20 border-2 border-white dark:border-slate-800">
                {student.name ? student.name.charAt(0).toUpperCase() : <User size={32} />}
              </div>
              <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-500 border-2 border-white dark:border-slate-900 rounded-full flex items-center justify-center" title="Siswa Aktif">
                <CheckCircle2 size={12} className="text-white" />
              </div>
            </div>

            {/* Student Details */}
            <div className="space-y-1.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="px-2.5 py-0.5 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-900/40 rounded-lg text-[10px] font-black uppercase tracking-wider">
                  Siswa Aktif
                </span>
                {student.classRole && (
                  <span className="px-2.5 py-0.5 bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-300/60 dark:border-amber-800/60 rounded-lg text-[10px] font-black uppercase tracking-wider">
                    {student.classRole}
                  </span>
                )}
              </div>
              <h3 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight">
                {student.name}
              </h3>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-semibold text-slate-500 dark:text-slate-400">
                <span className="flex items-center gap-1.5">
                  <GraduationCap size={15} className="text-indigo-500" />
                  Kelas <strong className="text-slate-800 dark:text-slate-200">{student.class}</strong>
                </span>
                <span className="text-slate-300 dark:text-slate-700">•</span>
                <span className="font-mono">
                  NISN: <strong className="text-slate-800 dark:text-slate-200">{student.nisn || student.nis || '-'}</strong>
                </span>
                <span className="text-slate-300 dark:text-slate-700">•</span>
                <span>
                  Jenis Kelamin: <strong className="text-slate-800 dark:text-slate-200">{student.gender || '-'}</strong>
                </span>
              </div>
            </div>
          </div>

          {/* Quick Stats Badges */}
          <div className="flex items-center gap-3 w-full sm:w-auto border-t sm:border-t-0 pt-4 sm:pt-0 border-slate-100 dark:border-slate-800">
            <div className="flex-1 sm:flex-initial p-3 px-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-800 text-center min-w-[100px]">
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-0.5">Kehadiran</span>
              <span className="text-lg font-black text-indigo-600 dark:text-indigo-400 font-mono">
                {stats.length > 0 ? `${stats[0].attendanceRate}%` : '100%'}
              </span>
            </div>
            <div className="flex-1 sm:flex-initial p-3 px-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-800 text-center min-w-[100px]">
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-0.5">Mapel</span>
              <span className="text-lg font-black text-slate-900 dark:text-white font-mono">
                {availableSubjects.length}
              </span>
            </div>
          </div>
        </div>
      </div>

      {activeTab === 'profil' ? (
        <>
          {/* Subject Selector (Enhanced pill design with glowing indicators) */}
          <div className="space-y-2.5">
            <div className="flex items-center gap-2 ml-1">
              <span className="w-1.5 h-3 bg-indigo-500 rounded-full"></span>
              <span className="text-[10px] font-black uppercase text-indigo-500/95 dark:text-indigo-400 tracking-widest">
                Pilih Mata Pelajaran
              </span>
            </div>
            <div className="w-full overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide">
              <div className="flex gap-2 min-w-max p-0.5">
                {availableSubjects.map(subject => {
                  const isSelected = isSubjectMatching(selectedSubject, subject);
                  return (
                    <button
                      key={subject}
                      onClick={() => setSelectedSubject(subject)}
                      className={`px-4.5 py-2.5 rounded-xl text-xs font-extrabold transition-all duration-200 flex items-center gap-2.5 border hover:scale-[1.02] active:scale-[0.98] cursor-pointer ${
                        isSelected 
                          ? 'bg-gradient-to-r from-indigo-600 to-indigo-700 text-white shadow-lg shadow-indigo-500/15 border-indigo-500/30' 
                          : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/60 hover:border-slate-300 dark:hover:border-slate-700'
                      }`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                        isSelected 
                          ? 'bg-amber-300 shadow-[0_0_8px_#f59e0b] animate-pulse scale-125' 
                          : 'bg-slate-300 dark:bg-slate-750'
                      }`} />
                      <span className="tracking-tight">{subject}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Attendance Dashboard Card */}
          <div className="bg-white dark:bg-slate-900 p-6 sm:p-7 rounded-3xl border border-slate-200/80 dark:border-slate-800 shadow-sm animate-fade-in space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 dark:border-slate-800 pb-4">
              <div>
                <h4 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-wider flex items-center gap-2">
                  <Calendar size={18} className="text-indigo-500" />
                  Presensi Kehadiran Pembelajaran
                </h4>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  Rekapitulasi kehadiran untuk mata pelajaran <strong className="text-indigo-600 dark:text-indigo-400">{selectedSubject || 'Semua'}</strong>
                </p>
              </div>
              {stats.length > 0 && stats[0].total > 0 && (
                <span className="text-xs font-bold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800/80 px-3 py-1 rounded-xl border border-slate-200/80 dark:border-slate-700/80 self-start sm:self-auto">
                  Total <strong className="text-indigo-600 dark:text-indigo-400">{stats[0].total}</strong> Pertemuan
                </span>
              )}
            </div>
            
            {isLoadingAttendance ? (
              <div className="py-12 flex flex-col items-center justify-center space-y-2">
                <Loader2 className="text-indigo-500 animate-spin" size={28} />
                <p className="text-xs font-black text-slate-400 uppercase tracking-widest">Memproses Presensi Kehadiran...</p>
              </div>
            ) : stats.length > 0 ? (
              stats.map(s => (
                <div key={s.subject} className="space-y-6">
                  {/* Attendance Rate Banner */}
                  <div className="p-4 sm:p-5 bg-gradient-to-r from-indigo-50/80 via-purple-50/40 to-slate-50 dark:from-indigo-950/40 dark:via-purple-950/20 dark:to-slate-900 rounded-2xl border border-indigo-100 dark:border-indigo-900/30 flex items-center justify-between gap-4">
                    <div className="space-y-1">
                      <span className="text-[10px] font-black uppercase tracking-widest text-indigo-600 dark:text-indigo-400 block">
                        Tingkat Kehadiran
                      </span>
                      <h5 className="text-base sm:text-lg font-black text-slate-900 dark:text-white">
                        {s.subject}
                      </h5>
                    </div>
                    <div className="text-right shrink-0">
                      <span className="text-2xl sm:text-3xl font-black text-indigo-600 dark:text-indigo-400 font-mono tracking-tight">
                        {s.attendanceRate}%
                      </span>
                      <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 block uppercase tracking-wider">
                        {s.hadir + s.dispen} dari {s.total} Hadir
                      </span>
                    </div>
                  </div>

                  {/* Status Summary Pills Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5 text-center text-xs font-bold">
                    <div className="p-3 bg-emerald-50 dark:bg-emerald-950/30 rounded-2xl text-emerald-800 dark:text-emerald-300 border border-emerald-200/60 dark:border-emerald-900/40 space-y-1">
                      <span className="text-[9px] font-black uppercase text-emerald-600 dark:text-emerald-400 block tracking-widest" title="Hadir">Hadir</span>
                      <span className="font-mono text-base font-black block">{s.hadir}</span>
                    </div>
                    <div className="p-3 bg-amber-50 dark:bg-amber-950/30 rounded-2xl text-amber-800 dark:text-amber-300 border border-amber-200/60 dark:border-amber-900/40 space-y-1">
                      <span className="text-[9px] font-black uppercase text-amber-600 dark:text-amber-400 block tracking-widest" title="Sakit">Sakit</span>
                      <span className="font-mono text-base font-black block">{s.sakit}</span>
                    </div>
                    <div className="p-3 bg-blue-50 dark:bg-blue-950/30 rounded-2xl text-blue-800 dark:text-blue-300 border border-blue-200/60 dark:border-blue-900/40 space-y-1">
                      <span className="text-[9px] font-black uppercase text-blue-600 dark:text-blue-400 block tracking-widest" title="Izin">Izin</span>
                      <span className="font-mono text-base font-black block">{s.izin}</span>
                    </div>
                    <div className="p-3 bg-purple-50 dark:bg-purple-950/30 rounded-2xl text-purple-800 dark:text-purple-300 border border-purple-200/60 dark:border-purple-900/40 space-y-1">
                      <span className="text-[9px] font-black uppercase text-purple-600 dark:text-purple-400 block tracking-widest" title="Dispensasi">Dispen</span>
                      <span className="font-mono text-base font-black block">{s.dispen}</span>
                    </div>
                    <div className="p-3 bg-rose-50 dark:bg-rose-950/30 rounded-2xl text-rose-800 dark:text-rose-300 border border-rose-200/60 dark:border-rose-900/40 space-y-1 col-span-2 sm:col-span-1">
                      <span className="text-[9px] font-black uppercase text-rose-600 dark:text-rose-400 block tracking-widest" title="Alpa">Alpa</span>
                      <span className="font-mono text-base font-black block">{s.alpa}</span>
                    </div>
                  </div>

                    {/* Detailed History List */}
                    {s.historyList && s.historyList.length > 0 ? (
                      <div className="pt-2 space-y-2">
                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1">
                          Riwayat Pertemuan ({s.historyList.length})
                        </span>
                        <div className="max-h-60 overflow-y-auto space-y-2 pr-1 scrollbar-thin">
                          {s.historyList.map((item, idx) => {
                            const statusLower = (item.status || '').toString().trim().toLowerCase();
                            let badgeClass = 'bg-slate-100 text-slate-700 border-slate-200';
                            if (statusLower === AttendanceStatus.Hadir.toLowerCase()) {
                              badgeClass = 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800/50';
                            } else if (statusLower === AttendanceStatus.Sakit.toLowerCase()) {
                              badgeClass = 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 border-amber-200 dark:border-amber-800/50';
                            } else if (statusLower === AttendanceStatus.Izin.toLowerCase()) {
                              badgeClass = 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 border-blue-200 dark:border-blue-800/50';
                            } else if (statusLower === AttendanceStatus.Dispen.toLowerCase()) {
                              badgeClass = 'bg-purple-50 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300 border-purple-200 dark:border-purple-800/50';
                            } else if (statusLower === AttendanceStatus.Alpa.toLowerCase()) {
                              badgeClass = 'bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300 border-rose-200 dark:border-rose-800/50';
                            }

                            return (
                              <div 
                                key={item.id || idx} 
                                className="p-3 bg-slate-50/70 dark:bg-slate-800/30 rounded-xl border border-slate-100 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 text-[11px]"
                              >
                                <div className="min-w-0 flex-1 space-y-1">
                                  <div className="flex items-center gap-2">
                                    <span className="text-[10px] font-mono font-bold text-slate-500 dark:text-slate-400">
                                      {item.date}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400">Topik / Materi Pokok: </span>
                                    <span className="font-bold text-slate-800 dark:text-slate-200">
                                      {item.topic || item.agendaTitle || '-'}
                                    </span>
                                  </div>
                                  {item.description && (
                                    <p className="text-[10px] text-slate-500 dark:text-slate-400 italic">
                                      "{item.description}"
                                    </p>
                                  )}
                                  {item.materialLink && (
                                    <div className="pt-1">
                                      <a 
                                        href={item.materialLink.startsWith('http') ? item.materialLink : `https://${item.materialLink}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1.5 text-[10px] font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 bg-indigo-50 dark:bg-indigo-950/60 px-2.5 py-1 rounded-lg border border-indigo-200/70 dark:border-indigo-800/70 transition-all shadow-sm"
                                      >
                                        <ExternalLink size={12} className="shrink-0" />
                                        <span>Link Materi Pembelajaran</span>
                                      </a>
                                    </div>
                                  )}
                                </div>

                                <span className={`self-start sm:self-center px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider border shrink-0 ${badgeClass}`}>
                                  {item.status || 'Hadir'}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="p-4 text-center rounded-xl border border-dashed border-slate-200 dark:border-slate-800 bg-slate-50/30 dark:bg-slate-800/20">
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                          Belum ada pertemuan tercatat untuk {s.subject}
                        </p>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-center text-[10px] text-slate-400 italic py-4">- Tidak ada data presensi -</p>
              )}
            </div>
        </>
      ) : (
        /* ================= NILAI SAYA TAB ================= */
        <div className="space-y-6 animate-fade-in">
          {/* Subject Selector Pills for Nilai */}
          <div className="space-y-2.5">
            <div className="flex items-center justify-between ml-1">
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-3 bg-amber-500 rounded-full"></span>
                <span className="text-[10px] font-black uppercase text-amber-600 dark:text-amber-400 tracking-widest">
                  Pilih Mata Pelajaran Untuk Detail TP
                </span>
              </div>
              <span className="text-[10px] text-slate-400 font-bold">
                Mapel Aktif: <strong className="text-slate-800 dark:text-slate-200">{selectedSubject || 'Semua'}</strong>
              </span>
            </div>
            <div className="w-full overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide">
              <div className="flex gap-2 min-w-max p-0.5">
                {availableSubjects.map(subject => {
                  const isSelected = isSubjectMatching(selectedSubject, subject);
                  return (
                    <button
                      key={subject}
                      onClick={() => setSelectedSubject(subject)}
                      className={`px-4.5 py-2.5 rounded-xl text-xs font-extrabold transition-all duration-200 flex items-center gap-2.5 border hover:scale-[1.02] active:scale-[0.98] cursor-pointer ${
                        isSelected 
                          ? 'bg-gradient-to-r from-amber-500 to-indigo-600 text-white shadow-lg shadow-indigo-500/15 border-amber-400/30' 
                          : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/60'
                      }`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                        isSelected 
                          ? 'bg-amber-300 shadow-[0_0_8px_#f59e0b] animate-pulse scale-125' 
                          : 'bg-slate-300 dark:bg-slate-750'
                      }`} />
                      <span className="tracking-tight">{subject}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Unified Capaian Card for Selected Subject */}
          {isGradeVisible ? (
            <div className="bg-slate-900 text-white p-6 sm:p-8 rounded-2xl shadow-xl shadow-indigo-500/5 relative overflow-hidden border border-slate-800 space-y-8 animate-fade-in">
              <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full -mr-32 -mt-32 blur-3xl pointer-events-none"></div>
              
              {/* Academic Achievements Header */}
              <div className="relative z-10 space-y-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-[8px] font-black uppercase text-amber-300 tracking-widest bg-amber-500/20 px-2 py-0.5 rounded border border-amber-400/20">
                      Nilai Utama
                    </span>
                    <div className="text-slate-300 font-mono text-[11px] uppercase font-bold tracking-widest">
                      {selectedSubject || 'Semua Mata Pelajaran'}
                    </div>
                  </div>
                  <span className="text-[10px] font-bold text-slate-400 bg-white/5 px-2.5 py-1 rounded-lg border border-white/10">
                    Siswa: <strong className="text-amber-300">{student.name}</strong> ({student.class})
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {/* Sumatif Akhir */}
                  <div className="p-4 sm:p-5 bg-white/5 backdrop-blur-md rounded-xl border border-white/10 flex flex-col items-center justify-center text-center">
                    <span className="text-[8px] sm:text-[9px] font-black text-indigo-200 uppercase tracking-widest mb-2">Nilai Sumatif Akhir (Evaluasi)</span>
                    {studentGrades.sas ? (
                      isSasVisible ? (
                        <span className="text-2xl sm:text-3xl font-black text-amber-400 tracking-tight font-mono">{studentGrades.sas.score}</span>
                      ) : (
                        <div className="flex items-center gap-1 text-slate-400 text-xs py-1">
                          <EyeOff size={13} className="text-slate-500" />
                          <span className="text-[10px] font-black uppercase text-slate-500 tracking-wider">Dikunci</span>
                        </div>
                      )
                    ) : (
                      <span className="text-xs text-slate-500 font-bold py-1">- Belum Ada -</span>
                    )}
                  </div>

                  {/* Nilai Rapor (DITONJOLKAN TAMPILANNYA SEPERTI SANGAT KONTRASTIF) */}
                  <div className="p-4 sm:p-5 bg-gradient-to-br from-indigo-600 to-indigo-800 rounded-xl shadow-xl border border-indigo-500/30 flex flex-col items-center justify-center relative overflow-hidden group">
                    <span className="text-[8px] sm:text-[9px] font-black text-indigo-100 uppercase tracking-widest mb-1 group-hover:scale-105 transition-transform">Nilai Rapor</span>
                    {isRaporVisible ? (
                      <div className="flex items-center gap-2.5 mt-1">
                        <span className="text-4xl sm:text-5xl font-black text-white font-mono tracking-tighter drop-shadow-[0_0_15px_rgba(255,255,255,0.45)]">
                          {typeof studentGrades.final === 'number' ? Math.round(studentGrades.final) : studentGrades.final}
                        </span>
                        <div className="flex flex-col gap-0.5">
                          <span className={`text-[8px] font-black uppercase px-1.5 py-0.5 rounded shadow-md border ${
                            studentGrades.final >= (schoolSettings?.minGrade || 75) 
                              ? 'bg-emerald-500/25 text-emerald-200 border-emerald-500/30' 
                              : 'bg-rose-500/25 text-rose-200 border-rose-500/30'
                          }`}>
                            {studentGrades.final >= (schoolSettings?.minGrade || 75) ? 'TUNTAS' : 'REMEDI'}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 text-indigo-200 text-xs py-2">
                        <Loader2 size={13} className="animate-spin" />
                        <span className="text-[10px] font-bold">Memproses</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Separator Divider */}
              <div className="border-t border-slate-800/80 my-2"></div>

              {/* Daftar Nilai TP Section */}
              <div className="relative z-10 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-black uppercase tracking-widest flex items-center gap-2 text-slate-300">
                    <ClipboardList size={14} className="text-indigo-400" />
                    Daftar Nilai TP ({selectedSubject})
                  </h4>
                </div>

                {(studentGrades.summative || []).length > 0 ? (
                  <div className="divide-y divide-slate-800">
                    {(() => {
                      const grouped = new Map<string, { tpId: string, sumatif?: { score: number, date: string }, praktikum?: { score: number, date: string } }>();
                      (studentGrades.summative || []).forEach(grade => {
                        if (!grouped.has(grade.tpId)) grouped.set(grade.tpId, { tpId: grade.tpId });
                        const item = grouped.get(grade.tpId)!;
                        if (grade.type === ActivityType.SUMATIF) item.sumatif = { score: grade.score, date: grade.date };
                        else if (grade.type === ActivityType.PRAKTIKUM) item.praktikum = { score: grade.score, date: grade.date };
                        else item.sumatif = { score: grade.score, date: grade.date };
                      });
                      
                      const sortedGroups = Array.from(grouped.values()).sort((a, b) => {
                        const tpA = (objectives || []).find(o => o.id === a.tpId);
                        const tpB = (objectives || []).find(o => o.id === b.tpId);
                        return (tpA?.code || '').localeCompare(tpB?.code || '', undefined, { numeric: true, sensitivity: 'base' });
                      });
                      
                      return sortedGroups.map((group, idx) => {
                        const tp = (objectives || []).find(o => o.id === group.tpId);
                        return (
                          <div key={idx} className="py-3 first:pt-0 last:pb-0 flex items-center justify-between gap-3 hover:bg-white/5 px-2 rounded-xl transition-colors">
                            <div className="flex items-center gap-2 min-w-0">
                              <span 
                                className="px-2.5 py-1.5 bg-indigo-500/10 text-indigo-300 rounded-lg text-xs font-black tracking-widest border border-indigo-500/20 shrink-0"
                                title={tp?.description || 'Tujuan Pembelajaran'}
                              >
                                {tp?.code || 'TP'}
                              </span>
                              <p className="text-xs font-medium text-slate-300 truncate" title={tp?.description}>
                                {tp?.description || 'Tujuan Pembelajaran'}
                              </p>
                              {tp?.semester && (
                                <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider bg-slate-800 px-2 py-0.5 rounded-md border border-slate-700 shrink-0">
                                  Sem {tp.semester}
                                </span>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-4 shrink-0">
                              {group.sumatif && (
                                <div className="text-right">
                                  <span className="text-[7px] font-bold text-slate-500 uppercase block tracking-wider leading-none mb-0.5">Sumatif</span>
                                  <span className="text-sm font-black text-indigo-300 font-mono">{group.sumatif.score}</span>
                                </div>
                              )}
                              {group.praktikum && (
                                <div className="text-right border-l border-slate-800 pl-3">
                                  <span className="text-[7px] font-bold text-slate-500 uppercase block tracking-wider leading-none mb-0.5">Praktik</span>
                                  <span className="text-sm font-black text-teal-400 font-mono">{group.praktikum.score}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                ) : (
                  <div className="py-8 flex flex-col items-center justify-center text-center border border-dashed border-slate-800 rounded-xl bg-slate-900/40">
                    <ClipboardList className="text-slate-600 mb-2" size={20} />
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Belum ada nilai tujuan pembelajaran untuk {selectedSubject}</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="p-8 bg-slate-900 rounded-2xl border border-slate-800 text-center space-y-2">
              <Lock className="mx-auto text-amber-400 mb-2" size={28} />
              <h4 className="text-sm font-black text-white uppercase tracking-wider">Nilai Belum Dipublikasikan</h4>
              <p className="text-xs text-slate-400 max-w-md mx-auto">
                Nilai untuk mata pelajaran ini disembunyikan sementara oleh guru atau sekolah. Silakan hubungi wali kelas atau guru pengampu.
              </p>
            </div>
          )}

          {/* All Subjects Recap Table / Grid */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-4 sm:p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-4">
              <div>
                <h3 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-wider flex items-center gap-2">
                  <GraduationCap className="text-indigo-500" size={18} />
                  Rekapitulasi Nilai Semua Mata Pelajaran
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  Rangkuman hasil belajar siswa kelas {student.class}
                </p>
              </div>
              <span className="px-3 py-1 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 rounded-xl text-xs font-mono font-bold border border-indigo-100 dark:border-indigo-900/30 shrink-0">
                {allSubjectsGrades.length} Mapel
              </span>
            </div>

            {allSubjectsGrades.length > 0 ? (
              <>
                {/* Mobile Cards View */}
                <div className="block md:hidden space-y-3">
                  {allSubjectsGrades.map((item) => {
                    const isSelected = isSubjectMatching(selectedSubject, item.subject);
                    return (
                      <div
                        key={item.subject}
                        onClick={() => setSelectedSubject(item.subject)}
                        className={`p-3.5 rounded-xl border transition-all cursor-pointer space-y-3 ${
                          isSelected 
                            ? 'bg-indigo-50/70 dark:bg-indigo-950/30 border-indigo-300 dark:border-indigo-800 shadow-sm' 
                            : 'bg-slate-50/50 dark:bg-slate-800/30 border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2 border-b border-slate-200/60 dark:border-slate-800/80 pb-2.5">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className={`w-2 h-2 rounded-full shrink-0 ${isSelected ? 'bg-indigo-500' : 'bg-slate-300 dark:bg-slate-700'}`} />
                              <h4 className="text-xs sm:text-sm font-black text-slate-900 dark:text-white leading-tight">
                                {item.subject}
                              </h4>
                            </div>
                            <p className="text-[10px] sm:text-[11px] font-medium text-slate-500 dark:text-slate-400 mt-1 pl-4">
                              {item.teacherName}
                            </p>
                          </div>
                        </div>

                        <div className="space-y-1.5 py-1">
                          <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">Jabaran Nilai TP:</span>
                          {item.gradeVisible && item.tpBreakdown.length > 0 ? (
                            <div className="flex flex-wrap gap-1.5">
                              {item.tpBreakdown.map((tp, idx) => (
                                <span key={idx} className="px-2 py-1 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 rounded-lg text-[10px] font-bold border border-indigo-100 dark:border-indigo-900/30">
                                  {tp.type === 'Sumatif' ? 'Sumatif' : 'Praktik'} TP {tp.tpCode}: <strong className="font-mono">{tp.score}</strong>
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400 italic">- Belum ada nilai TP -</span>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-2 text-center pt-1 border-t border-slate-200/60 dark:border-slate-800/80">
                          <div className="bg-white/80 dark:bg-slate-900/60 p-2 rounded-lg border border-slate-200/50 dark:border-slate-800/50">
                            <span className="text-[8px] font-bold text-slate-400 uppercase block mb-0.5">Nilai Sumatif Akhir (Evaluasi)</span>
                            <span className="text-xs font-mono font-bold text-amber-600 dark:text-amber-400">
                              {item.sasVisible ? item.sasScore || '-' : '🔒'}
                            </span>
                          </div>
                          <div className="bg-white/80 dark:bg-slate-900/60 p-2 rounded-lg border border-slate-200/50 dark:border-slate-800/50">
                            <span className="text-[8px] font-bold text-slate-400 uppercase block mb-0.5">Nilai Rapor</span>
                            <span className="text-xs font-mono font-black text-slate-900 dark:text-white">
                              {item.raporVisible ? (typeof item.final === 'number' ? Math.round(item.final) : item.final || '-') : '🔒'}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="border-b border-slate-200 dark:border-slate-800 text-[10px] uppercase tracking-wider text-slate-400 font-black">
                        <th className="py-3 px-3">Mata Pelajaran</th>
                        <th className="py-3 px-3">Guru Pengampu</th>
                        <th className="py-3 px-3">Jabaran Nilai TP</th>
                        <th className="py-3 px-3 text-center">Nilai Sumatif Akhir (Evaluasi)</th>
                        <th className="py-3 px-3 text-center">Nilai Rapor</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60 font-medium">
                      {allSubjectsGrades.map((item) => {
                        const isSelected = isSubjectMatching(selectedSubject, item.subject);
                        return (
                          <tr 
                            key={item.subject}
                            onClick={() => setSelectedSubject(item.subject)}
                            className={`hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors cursor-pointer ${
                              isSelected ? 'bg-indigo-50/50 dark:bg-indigo-950/20' : ''
                            }`}
                          >
                            <td className="py-3 px-3 font-bold text-slate-900 dark:text-white flex items-center gap-2">
                              <span className={`w-2 h-2 rounded-full ${isSelected ? 'bg-indigo-500' : 'bg-slate-300 dark:bg-slate-700'}`} />
                              <span>{item.subject}</span>
                            </td>
                            <td className="py-3 px-3 text-slate-500 dark:text-slate-400 text-[11px]">
                              {item.teacherName}
                            </td>
                            <td className="py-3 px-3">
                              {item.gradeVisible && item.tpBreakdown.length > 0 ? (
                                <div className="flex flex-wrap gap-1.5 py-1">
                                  {item.tpBreakdown.map((tp, idx) => (
                                    <span key={idx} className="px-2 py-1 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 rounded-lg text-[10px] font-bold border border-indigo-100 dark:border-indigo-900/30">
                                      {tp.type === 'Sumatif' ? 'Sumatif' : 'Praktik'} TP {tp.tpCode}: <strong className="font-mono">{tp.score}</strong>
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-slate-400 italic text-[11px]">- Belum ada nilai -</span>
                              )}
                            </td>
                            <td className="py-3 px-3 text-center font-mono font-bold text-amber-600 dark:text-amber-400">
                              {item.sasVisible ? item.sasScore || '-' : '🔒'}
                            </td>
                            <td className="py-3 px-3 text-center font-mono font-black text-sm text-slate-900 dark:text-white">
                              {item.raporVisible ? (typeof item.final === 'number' ? Math.round(item.final) : item.final || '-') : '🔒'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className="py-8 text-center text-slate-400 text-xs italic">
                Belum ada data mata pelajaran untuk siswa ini.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentProfileView;

